import type { FulfillmentOrderLine } from "../types";
import type { EntryBatchChange, EntryExistingDraft } from "./confirmationEntryTypes";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

/** 只提交显式修改字段；历史空补丁不重算，基线改变时整体拒绝。 */
export function buildConfirmationEntryChange(drafts: EntryExistingDraft[], current: FulfillmentOrderLine[], additions: Partial<FulfillmentOrderLine>[]): EntryBatchChange {
  const change: EntryBatchChange = { updates: [], additions, removals: [] };
  for (const draft of drafts) {
    if (JSON.stringify(current.find((line) => line.id === draft.base.id)) !== JSON.stringify(draft.base)) throw new Error("编辑范围的主表已发生变化，草稿已保留，请核对或放弃后重新打开");
    if (draft.removed) { change.removals.push(draft.base.id); continue; }
    const patch = changedEntryFields(draft);
    if (!Object.keys(patch).length) continue;
    const next = { ...draft.base, ...patch };
    const errors = validateFulfillmentLineDraft(next);
    if (errors.length) throw new Error(`第${draft.base.lineNo}行：${errors.join("；")}`);
    if (["quantity", "billingCount", "unitPriceAmount"].some((key) => key in patch)) patch.amount = calculateFulfillmentLineAmount(next.quantity, next.billingCount, next.unitPriceAmount);
    change.updates.push({ id: draft.base.id, patch });
  }
  return change;
}

/** 同一十进制数的输入格式不算量价变化，避免重新输入原值改写历史金额。 */
function comparableNumber(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.normalize("NFKC").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return `${BigInt(whole)}.${fraction.padEnd(2, "0")}`;
}

/** 预览和应用共用字段差异规则，原值格式变化不触发重算。 */
export function changedEntryFields(draft: EntryExistingDraft): Partial<FulfillmentOrderLine> {
  return Object.fromEntries(Object.entries(draft.patch).map(([key, value]) => [key, ["quantity", "billingCount", "unitPriceAmount"].includes(key) && typeof value === "string" ? value.normalize("NFKC").trim() : value])
    .filter(([key, value]) => {
      const original = draft.base[key as keyof FulfillmentOrderLine];
      if (["quantity", "billingCount", "unitPriceAmount"].includes(String(key)) && comparableNumber(value) !== null) return comparableNumber(value) !== comparableNumber(original);
      return JSON.stringify(value) !== JSON.stringify(original);
    })) as Partial<FulfillmentOrderLine>;
}
